function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import moment from "moment";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { CssContainer } from "../../components/container";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import ServiceFactory from "../../service";
import { hasTenantFeature } from "../../functions/tenant-features";
import { loadBrandKit } from "../../functions/brand-kit";
import { authHeaders, apiRoot } from "../reports/common";
import account from "../../data/account";
import { INPUT } from "../../components/form-styles";
import { api, audienceApi, contentApi, qs, errMsg, toast, idStr, num, railLabel, statusLabel, normalizeLists, browserTimezone, ago, dateTime, isAdmin } from "./api";
import { AudienceSection, ContentSection, ContentEditor, WhenSection, StartFromPicker, stepDone, domainLabel, abTimeline, resendDate } from "./steps";
import { CONTENT_PICK_TOP } from "../email-automation/content-pick-list";
import PreviewPane from "../email-automation/preview-pane";

/*
 * New / edit campaign (BULK_EMAIL_MANAGEMENT §13). One page since the
 * 2026-10-09 Email redesign (NewCampaign mockup): a top bar (name, autosave
 * state, "Send me a test", the primary action), three numbered sections on
 * the left (1 Who it goes to · 2 What it says · 3 When it sends) and, on the
 * right, the live server preview plus "Before it goes out" (the old Review
 * checks). "Edit the email" opens the composer in a full-screen overlay.
 * Autosaved as a Draft: POST
 * campaigns on the first save, PUT campaigns/{id} after (409 `locked` →
 * banner, autosave stops). The audience estimate (§7.4 funnel) re-runs 600 ms
 * after any audience change through POST campaigns/{id}/estimate. "Start
 * from…" duplicates a Sent campaign (POST campaigns/{id}/duplicate) and
 * continues in the copy, same mounted instance. Review renders the real
 * email through the composer's preview rail (POST campaigns/{id}/preview),
 * drives POST test-send / lint, then Submit (POST submit) or Schedule (POST
 * schedule); the gate codes map to inline messages.
 */

const SAVE_DEBOUNCE = 900;
const ESTIMATE_DEBOUNCE = 600;
const PREVIEW_DEBOUNCE = 1500;

/** "9 AM" style label for schedule.localHour (default 10). */
function localHourText(h, fmt) {
  return moment().hour(h == null ? 10 : Number(h)).minute(0).format(fmt);
}

/** Lists or single people: either counts as someone to send to. */
function hasRecipients(a) {
  return !!a && ((a.include || []).length > 0 || (a.people || []).length > 0);
}
function freshModel() {
  return {
    id: null,
    name: "",
    kind: "broadcast",
    rail: "plunk",
    senderId: null,
    status: "Draft",
    containerId: null,
    containerTitle: "",
    audience: {
      include: [],
      exclude: [],
      people: [],
      excludeInactive: true,
      excludeEmailedDays: null
    },
    content: {
      source: "composer",
      designJson: null,
      html: "",
      text: "",
      subject: "",
      preheader: "",
      variantId: null
    },
    variants: {
      variants: [{
        key: "A",
        subject: ""
      }, {
        key: "B",
        subject: ""
      }],
      sample: 20,
      winnerMetric: "clicks",
      waitMinutes: 240,
      autoWinner: true
    },
    schedule: {
      mode: "now",
      sendAt: null,
      timezone: browserTimezone(),
      localHour: 10,
      localDate: null,
      throttlePerHour: null,
      cadence: null
    },
    settings: {}
  };
}
function fromDetail(d, prev) {
  const c = d.campaign || {};
  const a = d.audience || {};
  const v = d.variants;
  const base = prev || freshModel();
  return {
    ...base,
    id: idStr(c.id),
    name: c.name || "",
    kind: c.kind || "broadcast",
    rail: c.rail || "plunk",
    status: c.status || "Draft",
    senderId: c.senderId && String(idStr(c.senderId)) !== "0" ? idStr(c.senderId) : null,
    containerId: c.containerId ? idStr(c.containerId) : null,
    reviewStatus: c.reviewStatus,
    rejectReason: c.rejectReason,
    sendAt: c.sendAt,
    audience: {
      include: (a.include || []).map(idStr),
      exclude: (a.exclude || []).map(idStr),
      people: (a.people || []).map(idStr).filter(Boolean),
      excludeInactive: a.excludeInactive !== false,
      excludeEmailedDays: a.excludeEmailedDays == null ? null : Number(a.excludeEmailedDays)
    },
    content: {
      ...base.content,
      ...(d.content || {}),
      variantId: d.content && d.content.variantId ? idStr(d.content.variantId) : null
    },
    variants: v && Array.isArray(v.variants) && v.variants.length ? v : base.variants,
    schedule: {
      ...base.schedule,
      ...(d.schedule || {})
    },
    settings: d.settings || {},
    estimate: d.estimate || base.estimate || null,
    senderVerified: d.senderVerified,
    nextTargets: d.nextTargets || []
  };
}
function toPayload(c) {
  const mode = c.schedule.mode === "sto" ? "now" : c.schedule.mode || "now"; // send-time optimization retired 2026-10-04
  const p = {
    name: c.name,
    kind: c.kind,
    rail: c.rail || "plunk",
    audience: {
      include: (c.audience.include || []).map(String),
      exclude: (c.audience.exclude || []).map(String),
      people: (c.audience.people || []).map(String),
      excludeInactive: c.audience.excludeInactive !== false,
      excludeEmailedDays: c.audience.excludeEmailedDays == null ? null : Number(c.audience.excludeEmailedDays)
    },
    content: {
      source: c.content.source || "composer",
      designJson: c.content.designJson || null,
      html: c.content.html || null,
      text: c.content.text || null,
      subject: c.content.subject || null,
      preheader: c.content.preheader || null,
      variantId: c.content.variantId || null
    },
    schedule: {
      mode,
      sendAt: c.schedule.sendAt || null,
      timezone: c.schedule.timezone || browserTimezone(),
      localHour: c.schedule.localHour == null ? null : Number(c.schedule.localHour),
      localDate: mode === "local" && c.schedule.localDate != null ? c.schedule.localDate : null,
      throttlePerHour: c.schedule.throttlePerHour || null,
      cadence: c.kind === "recurring" ? c.schedule.cadence || {
        unit: "weekly",
        dayOfWeek: 2,
        hour: 9,
        leadHours: 24
      } : null
    },
    settings: {
      ...(c.settings || {}),
      resend: c.settings && c.settings.resend ? c.settings.resend : null
    }
  };
  if (c.senderId) p.senderId = String(c.senderId);
  if (c.containerId) p.containerId = String(c.containerId);
  if (c.kind === "ab_test") p.variants = c.variants;
  return p;
}
class EmailCampaignWizardComponent extends React.Component {
  constructor(props) {
    super(props);
    /** Delivery defaults (2026-10-04): the default sender identity and the default rail are preselected so step 3 is usually a glance. */
    _defineProperty(this, "applyDefaults", () => {
      const {
        c,
        senders,
        rails
      } = this.state;
      if (!c || this.locked()) return;
      const fields = {};
      if (!this._railDefaulted && !c.id && rails && rails.default) {
        this._railDefaulted = true;
        const def = String(rails.default).toLowerCase();
        const row = (rails.items || []).find(r => String(r.rail).toLowerCase() === def);
        const connected = def === "plunk" || !!(row && (row.connected || row.status === "connected"));
        if (connected && def !== "smartlead" && def !== (c.rail || "plunk")) fields.rail = def;
      }
      const rail = fields.rail || c.rail || "plunk";
      if (rail === "plunk" && !c.senderId && senders && senders.length) {
        const def = senders.find(s => s.isDefault) || senders[0];
        if (def) fields.senderId = def.id;
      }
      if (Object.keys(fields).length) this.patch(null, fields);
    });
    // ---- model edits + autosave ------------------------------------------------
    _defineProperty(this, "patch", (section, fields) => {
      const prev = this.state.c;
      const c = section ? {
        ...prev,
        [section]: {
          ...(prev[section] || {}),
          ...fields
        }
      } : {
        ...prev,
        ...fields
      };
      const audienceChanged = section === "audience";
      const contentChanged = section === "content" || section === "variants" || !section && (fields.kind || fields.rail || fields.senderId);
      const railChanged = !section && fields.rail && fields.rail !== prev.rail;
      if (railChanged) this._railDefaulted = true;
      this.setState({
        c
      }, () => {
        this.queueSave();
        if (audienceChanged) this.queueEstimate();
        if (contentChanged) this.queuePreview();
        if (railChanged && fields.rail !== "plunk") this.loadRailSenders(fields.rail);
        if (railChanged) this.applyDefaults();
      });
    });
    _defineProperty(this, "save", () => {
      clearTimeout(this._saveT);
      if (!this._alive) return Promise.resolve();
      if (this._saving) {
        this._pending = true;
        return this._saving;
      }
      const c = this.state.c;
      if (!c || !(c.name || "").trim() || this.locked()) {
        this._dirty = false;
        return Promise.resolve();
      }
      this._dirty = false;
      this.setState({
        saveState: "saving",
        saveErr: null
      });
      this._saving = (async () => {
        try {
          const payload = toPayload(c);
          let d;
          if (!c.id) {
            d = await api("POST", "/campaigns", payload);
            this._createdHere = true;
            if (!this._alive) return;
            const id = idStr(d && d.campaign && d.campaign.id || d && d.id);
            this._createdHere = true;
            this.setState({
              c: {
                ...this.state.c,
                id,
                status: d.campaign && d.campaign.status || "Draft",
                settings: d.settings || {}
              },
              saveState: "saved",
              savedAt: new Date()
            });
            this.props.history.replace(`/Email/campaigns/edit/${id}`);
          } else {
            d = await api("PUT", `/campaigns/${c.id}`, payload);
            if (!this._alive) return;
            // Keep the server's stamps (testSent, lintClean, rail state) without disturbing in-flight edits.
            const cur = this.state.c;
            this.setState({
              c: {
                ...cur,
                status: d.campaign && d.campaign.status || cur.status,
                settings: {
                  ...(d.settings || {}),
                  resend: cur.settings && cur.settings.resend ? cur.settings.resend : (d.settings || {}).resend || null
                }
              },
              saveState: "saved",
              savedAt: new Date()
            });
          }
        } catch (e) {
          if (!this._alive) return;
          if (e.code === "locked") this.setState({
            saveState: "locked",
            saveErr: errMsg(e)
          });else this.setState({
            saveState: "error",
            saveErr: errMsg(e, "Autosave failed.")
          });
        } finally {
          this._saving = null;
          if (this._pending) {
            this._pending = false;
            this.save();
          }
        }
      })();
      return this._saving;
    });
    _defineProperty(this, "ensureSaved", async () => {
      if (this._saving) await this._saving;
      if (this._dirty || !this.id()) await this.save();
      if (this._saving) await this._saving;
      return this.id();
    });
    _defineProperty(this, "estimate", async () => {
      const c = this.state.c;
      if (!c || !hasRecipients(c.audience)) {
        this.setState({
          estimate: null,
          estimating: false
        });
        return;
      }
      this.setState({
        estimating: true
      });
      try {
        const id = await this.ensureSaved();
        if (!id) {
          if (this._alive) this.setState({
            estimating: false
          });
          return;
        }
        const f = await api("POST", `/campaigns/${id}/estimate`);
        if (this._alive) this.setState({
          estimate: f,
          estimating: false
        });
      } catch (e) {
        if (this._alive) this.setState({
          estimating: false,
          actionErr: errMsg(e, "The estimate could not be computed.")
        });
      }
    });
    // ---- single recipients ("People") ------------------------------------------------
    /** Name and email for people saved on a draft without them (GET {activity}/contacts/{id}); failures fall back to "1 person". */
    _defineProperty(this, "loadPeopleInfo", ids => {
      const known = this.state.peopleInfo || {};
      (ids || []).map(idStr).filter(id => id && !known[id]).forEach(async id => {
        let info = {
          name: "",
          email: ""
        };
        try {
          const Service = ServiceFactory.getType("/contacts");
          const contact = await new Service().getAsync({
            id
          });
          const emails = (contact && contact.email || []).map(e => e && e.emailAddress).filter(Boolean);
          info = {
            name: String(contact && contact.fullName || "").trim(),
            email: emails[0] || ""
          };
        } catch (e) {/* keep the fallback label */}
        if (this._alive) this.setState({
          peopleInfo: {
            ...this.state.peopleInfo,
            [id]: info
          }
        });
      });
    });
    _defineProperty(this, "removePerson", id => {
      const people = (this.state.c.audience.people || []).filter(x => x !== id);
      this.patch("audience", {
        people
      });
    });
    _defineProperty(this, "loadRailSenders", async rail => {
      this.setState({
        railSenders: null
      });
      try {
        const d = await api("GET", `/rails/${rail}/senders`);
        if (this._alive) this.setState({
          railSenders: {
            items: d && d.items || []
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          railSenders: {
            items: [],
            error: errMsg(e, "The provider's senders could not be listed.")
          }
        });
      }
    });
    // ---- start from a past campaign ------------------------------------------------
    _defineProperty(this, "loadPastCampaigns", async () => {
      if (this.state.pastCampaigns) return;
      try {
        const d = await api("GET", `/campaigns${qs({
          status: "Sent",
          sort: "sentAt",
          $top: 50
        })}`);
        if (!this._alive) return;
        const items = (d && d.items || []).map(r => {
          const c = r.campaign || r;
          return {
            id: idStr(c.id),
            name: c.name || "",
            kind: c.kind,
            subject: r.subject || "",
            sentAt: c.completedDate || c.sendAt || null
          };
        }).filter(r => r.id);
        this.setState({
          pastCampaigns: items
        });
      } catch (e) {
        if (this._alive) this.setState({
          pastCampaigns: []
        });
      }
    });
    /** Duplicate the picked Sent campaign and continue editing the copy here (history.replace, same mounted instance). */
    _defineProperty(this, "startFrom", async picked => {
      if (this.locked() || this.state.startFromBusy) return;
      this.setState({
        startFromBusy: true,
        actionErr: null
      });
      clearTimeout(this._saveT);
      try {
        if (this._saving) await this._saving;
        const cur = this.state.c;
        const typedName = (cur.name || "").trim();
        const body = typedName ? {
          name: typedName
        } : {};
        const d = await api("POST", `/campaigns/${picked.id}/duplicate`, body);
        if (!this._alive) return;
        const newId = idStr(d && d.campaign && d.campaign.id || d && d.id);
        if (!newId) throw new Error("The copy was created without an id.");
        const detail = await api("GET", `/campaigns/${newId}`);
        if (!this._alive) return;
        // The empty Draft this instance minted a moment ago is superseded by the copy; drop it quietly.
        if (cur.id && this._createdHere && cur.id !== newId) api("DELETE", `/campaigns/${cur.id}`).catch(() => {});
        const c = fromDetail(detail);
        this._dirty = false;
        this._createdHere = true;
        this._railDefaulted = true;
        this.setState({
          c,
          estimate: c.estimate || null,
          startedFrom: {
            id: picked.id,
            name: picked.name
          },
          startFromBusy: false,
          saveState: "saved",
          savedAt: new Date(),
          preview: null,
          previewPerson: null,
          lint: {
            busy: false,
            result: null,
            err: null
          }
        }, () => {
          if (c.rail !== "plunk") this.loadRailSenders(c.rail);
          this.applyDefaults();
          this.queueEstimate();
          this.loadPeopleInfo(c.audience.people);
          if (stepDone(c, 2)) this.preview(null);
        });
        this.props.history.replace(`/Email/campaigns/edit/${newId}`);
        toast(`Started from “${picked.name}”.`);
      } catch (e) {
        if (this._alive) this.setState({
          startFromBusy: false,
          actionErr: errMsg(e, "That campaign could not be copied.")
        });
      }
    });
    // ---- content helpers ---------------------------------------------------------
    _defineProperty(this, "renderPreview", async (design, opts) => {
      const body = {
        design,
        firstName: "Dana"
      };
      if (opts && opts.personId) {
        body.personId = opts.personId;
        delete body.firstName;
      }
      const res = await fetch(`${apiRoot()}/campaign/email/render`, {
        method: "POST",
        headers: {
          ...authHeaders(),
          "content-type": "application/json"
        },
        body: JSON.stringify(body)
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Preview failed (${res.status}).`);
      return d;
    });
    /** The Review-step render of the whole campaign (POST campaigns/{id}/preview?personId=). */
    _defineProperty(this, "preview", async personId => {
      const c = this.state.c;
      if (!c || !stepDone(c, 2)) {
        this.setState({
          preview: {
            err: "Add a subject and the email to see the preview."
          }
        });
        return;
      }
      this.setState({
        preview: {
          ...(this.state.preview || {}),
          busy: true,
          err: null
        }
      });
      try {
        const id = await this.ensureSaved();
        if (!id) {
          if (this._alive) this.setState({
            preview: {
              err: "Name the campaign so it can be saved, then the preview appears."
            }
          });
          return;
        }
        const d = await api("POST", `/campaigns/${id}/preview${qs({
          personId
        })}`);
        if (this._alive) this.setState({
          preview: {
            html: d.html,
            subject: d.subject,
            person: d.person,
            sample: d.sample
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          preview: {
            err: errMsg(e, "Preview failed.")
          }
        });
      }
    });
    _defineProperty(this, "setPreviewPerson", person => {
      this.setState({
        previewPerson: person
      }, () => this.preview(person ? person.id : null));
    });
    _defineProperty(this, "loadContentPicks", async () => {
      if (this.state.contentPicks) return;
      try {
        const d = await contentApi("GET", `/containers/this_quarter?$top=${CONTENT_PICK_TOP}`);
        if (!this._alive) return;
        const items = (d && d.items || []).slice();
        const metric = x => Number(x.salesValue || 0) * 1e6 + Number(x.conversions || 0) * 1e3 + Number(x.sessions || 0);
        items.sort((a, b) => metric(b) - metric(a));
        this.setState({
          contentPicks: items
        });
      } catch (e) {
        if (this._alive) this.setState({
          contentPicks: []
        });
      }
    });
    _defineProperty(this, "pickContainer", async p => {
      try {
        const d = await contentApi("GET", `/containers/detail/${idStr(p.id)}`);
        if (!this._alive) return;
        const email = (d && d.variants || []).find(v => v.channel === "email" && (v.body || "").trim());
        if (!email) {
          toast("That piece has no email variant yet.");
          return;
        }
        const c = this.state.c;
        this.setState({
          c: {
            ...c,
            containerId: idStr(p.id),
            containerTitle: p.title || "",
            content: {
              ...c.content,
              source: "content_hub",
              variantId: idStr(email.id),
              html: email.body,
              subject: c.content.subject || email.subject || email.title || p.title || ""
            }
          }
        }, () => {
          this.queueSave();
          this.queuePreview();
        });
      } catch (e) {
        toast(errMsg(e, "The piece could not be read."));
      }
    });
    // ---- review actions --------------------------------------------------------
    _defineProperty(this, "sendTest", async toList => {
      const to = Array.isArray(toList) ? toList : this.state.test.to.split(/[,\s;]+/).map(x => x.trim()).filter(Boolean);
      if (!to.length) {
        this.setState({
          test: {
            ...this.state.test,
            err: "Add at least one address."
          }
        });
        return;
      }
      this.setState({
        test: {
          ...this.state.test,
          busy: true,
          err: null
        }
      });
      try {
        const id = await this.ensureSaved();
        const d = await api("POST", `/campaigns/${id}/test-send`, {
          to
        });
        if (!this._alive) return;
        const c = this.state.c;
        this.setState({
          test: {
            ...this.state.test,
            busy: false,
            open: false,
            result: d
          },
          c: {
            ...c,
            settings: {
              ...c.settings,
              testSent: true,
              testSentDate: d.testSentDate,
              testSentTo: d.to
            }
          }
        });
        toast(`Test sent to ${(d.to || to).join(", ")}.`);
      } catch (e) {
        if (this._alive) this.setState({
          test: {
            ...this.state.test,
            busy: false,
            err: errMsg(e, "The test could not be sent.")
          }
        });
      }
    });
    /** "Send me a test" (top bar) and "Send test to me" from the preview. */
    _defineProperty(this, "sendTestToMe", () => {
      const me = account.getEmail && account.getEmail() || "";
      if (!me) {
        this.setState({
          test: {
            ...this.state.test,
            open: true,
            err: "Your address is not known; type one."
          }
        });
        return;
      }
      this.setState({
        test: {
          ...this.state.test,
          to: this.state.test.to || me
        }
      });
      return this.sendTest([me]);
    });
    _defineProperty(this, "lint", async () => {
      this.setState({
        lint: {
          busy: true,
          result: null,
          err: null
        }
      });
      try {
        const id = await this.ensureSaved();
        const d = await api("POST", `/campaigns/${id}/lint`);
        if (!this._alive) return;
        const c = this.state.c;
        this.setState({
          lint: {
            busy: false,
            result: d
          },
          c: {
            ...c,
            settings: {
              ...c.settings,
              lintClean: d.clean,
              lintDate: d.runDate
            },
            senderVerified: d.senderVerified
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          lint: {
            busy: false,
            err: errMsg(e, "The checks could not run.")
          }
        });
      }
    });
    _defineProperty(this, "transition", async (action, body) => {
      this.setState({
        busy: true,
        actionErr: null
      });
      try {
        const id = await this.ensureSaved();
        const d = await api("POST", `/campaigns/${id}/${action}`, body);
        if (!this._alive) return;
        toast(action === "submit" ? "Submitted for review." : "Scheduled.");
        this.props.history.push(`/Email/campaigns/detail/${id}`);
        return d;
      } catch (e) {
        if (this._alive) this.setState({
          busy: false,
          actionErr: errMsg(e, "The action failed.")
        });
      }
    });
    _defineProperty(this, "schedule", () => {
      const s = this.state.c.schedule;
      const mode = s.mode === "sto" ? "now" : s.mode || "now";
      return this.transition("schedule", {
        mode,
        sendAt: mode === "at" ? s.sendAt : null,
        timezone: s.timezone || browserTimezone(),
        localHour: mode === "local" ? s.localHour : null,
        localDate: mode === "local" && s.localDate ? s.localDate : null,
        throttlePerHour: s.throttlePerHour || null
      });
    });
    _defineProperty(this, "saveAndClose", async () => {
      const id = await this.ensureSaved();
      this.props.history.push(id ? `/Email/campaigns/detail/${id}` : "/Email/campaigns");
    });
    /** Back arrow: flush the autosave, then return to the campaign list. */
    _defineProperty(this, "back", async () => {
      try {
        await this.ensureSaved();
      } catch (e) {/* autosave errors already show */}
      this.props.history.push("/Email/campaigns");
    });
    // ---- editor overlay + section navigation --------------------------------------------
    _defineProperty(this, "openEditor", opts => {
      this.setState({
        editing: {
          hubOpen: !!(opts && opts.hubOpen)
        }
      });
      document.addEventListener("keydown", this.onKey);
    });
    _defineProperty(this, "closeEditor", () => {
      document.removeEventListener("keydown", this.onKey);
      this.setState({
        editing: null
      }, () => {
        if (stepDone(this.state.c, 2)) this.preview(this.state.previewPerson ? this.state.previewPerson.id : null);
      });
    });
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape" && this.state.editing) this.closeEditor();
    });
    /** "Fix it" links in the checklist: scroll to the section and flash it. */
    _defineProperty(this, "goTo", n => {
      const el = this.sec[n] && this.sec[n].current;
      if (el && el.scrollIntoView) el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      clearTimeout(this._flashT);
      this.setState({
        flash: n
      });
      this._flashT = setTimeout(() => this._alive && this.setState({
        flash: null
      }), 1600);
    });
    _defineProperty(this, "openTestForm", () => {
      const me = account.getEmail && account.getEmail() || "";
      this.setState({
        test: {
          ...this.state.test,
          open: true,
          to: this.state.test.to || me
        }
      });
    });
    this.state = {
      c: null,
      loadErr: null,
      saveState: "idle",
      savedAt: null,
      saveErr: null,
      lists: null,
      senders: null,
      rails: null,
      wsSettings: null,
      brand: null,
      railSenders: null,
      estimate: null,
      estimating: false,
      preview: null,
      previewPerson: null,
      contentPicks: null,
      pastCampaigns: null,
      startedFrom: null,
      startFromBusy: false,
      test: {
        open: false,
        to: "",
        busy: false,
        result: null,
        err: null
      },
      lint: {
        busy: false,
        result: null,
        err: null
      },
      actionErr: null,
      busy: false,
      editing: null,
      startFromOpen: false,
      flash: null,
      peopleInfo: {} // personId -> { name, email } for the "People" chips
    };
    this.sec = {
      1: /*#__PURE__*/React.createRef(),
      2: /*#__PURE__*/React.createRef(),
      3: /*#__PURE__*/React.createRef()
    };
    this._alive = true;
    this._dirty = false;
    this._createdHere = false; // the Draft id was minted by this instance's first autosave (safe to drop when "Start from…" replaces it)
    this._railDefaulted = false;
  }
  id() {
    return this.state.c && this.state.c.id;
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    const id = this.props.params && this.props.params.id;
    const st = this.props.location && this.props.location.state || {};
    // Outreach has its own flow: a request for the outreach kind goes straight to the outreach wizard.
    if (!id) {
      let qKind = null;
      try {
        qKind = new URLSearchParams(this.props.location && this.props.location.search || "").get("kind");
      } catch (e) {/* old browser */}
      if ((st.kind === "outreach" || qKind === "outreach") && hasTenantFeature("outreach")) {
        this._alive = false;
        this.props.history.replace("/Email/campaigns/outreach/new", {
          name: st.name || ""
        });
        return;
      }
    }
    // Reference data, each independent of the campaign.
    api("GET", "/senders").then(d => this._alive && this.setState({
      senders: (d && d.items || []).map(s => ({
        ...s,
        id: idStr(s.id)
      }))
    }, this.applyDefaults)).catch(() => this._alive && this.setState({
      senders: []
    }));
    api("GET", "/rails").then(d => this._alive && this.setState({
      rails: d || {
        items: [],
        default: "plunk"
      }
    }, this.applyDefaults)).catch(() => this._alive && this.setState({
      rails: {
        items: [],
        default: "plunk"
      }
    }));
    api("GET", "/settings").then(d => this._alive && this.setState({
      wsSettings: d || {}
    })).catch(() => this._alive && this.setState({
      wsSettings: {}
    }));
    audienceApi("GET", "").then(d => this._alive && this.setState({
      lists: normalizeLists(d)
    })).catch(() => this._alive && this.setState({
      lists: []
    }));
    loadBrandKit().then(b => this._alive && this.setState({
      brand: b || {}
    })).catch(() => {});
    if (id) {
      try {
        const d = await api("GET", `/campaigns/${id}`);
        if (!this._alive) return;
        const c = fromDetail(d);
        this._railDefaulted = true; // an existing campaign keeps its stored rail
        this.setState({
          c,
          estimate: c.estimate || null
        }, () => {
          if (stepDone(c, 2)) this.preview(null);
          if (!c.estimate && hasRecipients(c.audience) && !this.locked()) this.estimate();
          this.loadPeopleInfo(c.audience.people);
          if (c.rail !== "plunk") this.loadRailSenders(c.rail);
          this.applyDefaults();
        });
      } catch (e) {
        if (this._alive) this.setState({
          loadErr: errMsg(e, "This campaign could not be loaded.")
        });
      }
    } else {
      // Coming back from the Outreach wizard (or any caller) may hand over the name typed so far.
      // Prefill from the URL: ?audience=<listId> picks a list; ?person=<id>&name=&email= adds one person.
      let q = null;
      try {
        q = new URLSearchParams(this.props.location && this.props.location.search || "");
      } catch (e) {/* old browser */}
      const qList = q ? idStr((q.get("audience") || "").trim()) : "";
      const qPerson = q ? idStr((q.get("person") || "").trim()) : "";
      const qName = q ? (q.get("name") || "").trim() : "";
      const qEmail = q ? (q.get("email") || "").trim() : "";
      const base = freshModel();
      const audience = {
        ...base.audience,
        include: qList ? [qList] : [],
        people: qPerson ? [qPerson] : []
      };
      const name = st.name || (qPerson && (qName || qEmail) ? `Email to ${qName || qEmail}` : "");
      if (qPerson) this.setState({
        peopleInfo: {
          [qPerson]: {
            name: qName,
            email: qEmail
          }
        }
      });
      this.setState({
        c: {
          ...base,
          name,
          audience,
          kind: st.kind && st.kind !== "outreach" ? st.kind : "broadcast"
        }
      }, () => {
        this.applyDefaults();
        if (name) this.queueSave();
        if (hasRecipients(audience)) this.queueEstimate();
        if (qPerson && !qName && !qEmail) this.loadPeopleInfo([qPerson]);
      });
    }
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._saveT);
    clearTimeout(this._estT);
    clearTimeout(this._prevT);
    clearTimeout(this._flashT);
    document.removeEventListener("keydown", this.onKey);
  }
  locked() {
    const st = this.state.c && this.state.c.status;
    return this.state.saveState === "locked" || ["Scheduled", "Sending", "Paused", "Sent", "Canceled", "InReview"].indexOf(st) >= 0;
  }
  queueSave() {
    if (this.locked()) return;
    this._dirty = true;
    clearTimeout(this._saveT);
    this._saveT = setTimeout(() => this.save(), SAVE_DEBOUNCE);
  }
  /** Re-render the right-hand preview a moment after the email changes (not while the editor overlay is open; closing it re-renders). */
  queuePreview() {
    clearTimeout(this._prevT);
    this._prevT = setTimeout(() => {
      if (!this._alive || this.state.editing) return;
      if (stepDone(this.state.c, 2)) this.preview(this.state.previewPerson ? this.state.previewPerson.id : null);
    }, PREVIEW_DEBOUNCE);
  }
  queueEstimate() {
    clearTimeout(this._estT);
    this._estT = setTimeout(() => this.estimate(), ESTIMATE_DEBOUNCE);
  }
  renderPeople() {
    const {
      c,
      peopleInfo
    } = this.state;
    const people = c.audience.people || [];
    if (!people.length) return null;
    const locked = this.locked();
    return /*#__PURE__*/React.createElement("div", {
      className: "ecp-people"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-sublabel"
    }, "People"), /*#__PURE__*/React.createElement("div", {
      className: "ecc-chips"
    }, people.map(id => {
      const info = (peopleInfo || {})[id] || {};
      const label = info.name && info.email ? `${info.name} · ${info.email}` : info.name || info.email || "1 person";
      return /*#__PURE__*/React.createElement("span", {
        key: id,
        className: "ecc-chip ecp-chip"
      }, /*#__PURE__*/React.createElement("span", {
        className: "ecp-chip-text"
      }, label), !locked ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        "aria-label": `Remove ${info.name || info.email || "this person"}`,
        onClick: () => this.removePerson(id)
      }, "\u2715") : null);
    })), /*#__PURE__*/React.createElement("span", {
      className: "ecc-note"
    }, "Sending to these people as well as any lists you pick."));
  }
  // ---- render --------------------------------------------------------------------
  /** Review gate state shared by the top-bar action and the checklist. */
  gate() {
    const {
      c,
      wsSettings
    } = this.state;
    const sendWithoutReview = !!(wsSettings && wsSettings.sendWithoutReview);
    return {
      sendWithoutReview,
      canSchedule: c.status === "Approved" || sendWithoutReview && c.status === "Draft",
      canSubmit: c.status === "Draft" || c.status === "Rejected"
    };
  }

  /** "Send now" / "Schedule for Oct 21" / "Submit for review": the button says what happens. */
  renderPrimary() {
    const {
      c,
      busy
    } = this.state;
    if (this.locked()) return null;
    const st = c.settings || {};
    const {
      canSchedule,
      canSubmit
    } = this.gate();
    if (canSchedule) {
      const s = c.schedule;
      const mode = s.mode === "sto" ? "now" : s.mode || "now";
      const cad = s.cadence || {};
      const label = c.kind === "recurring" ? cad.unit === "monthly" ? "Start monthly sends" : "Start weekly sends" : mode === "at" ? s.sendAt ? `Schedule for ${moment(s.sendAt).format("MMM D")}` : "Schedule" : mode === "local" ? s.localDate ? `Schedule for ${moment(s.localDate, "YYYY-MM-DD").format("MMM D")}, ${localHourText(s.localHour, "h A")} their time` : `Send at ${localHourText(s.localHour, "h A")} their time` : "Send now";
      const missingDate = (mode === "at" && !s.sendAt || mode === "local" && s.localDate === "") && c.kind !== "recurring";
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ecc-btn ecc-btn-primary",
        disabled: busy || missingDate,
        title: missingDate ? "Pick a date first" : "",
        onClick: this.schedule
      }, busy ? "Working…" : label);
    }
    const why = !canSubmit ? "This campaign can't be submitted in its current state." : !st.testSent ? "Send yourself a test first" : "";
    return /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      disabled: busy || !canSubmit || !st.testSent,
      title: why,
      onClick: () => this.transition("submit")
    }, busy ? "Working…" : "Submit for review");
  }

  /** The old Review step's checks as one "Before it goes out" list: [{state: ok|warn, text, fix}]. */
  checks() {
    const {
      c,
      estimate,
      estimating,
      wsSettings,
      senders,
      lint
    } = this.state;
    const st = c.settings || {};
    const plunk = (c.rail || "plunk") === "plunk";
    const sender = (senders || []).find(s => s.id === c.senderId);
    const senderOk = plunk ? c.senderVerified != null ? !!c.senderVerified : !!(sender && sender.isVerified) : true;
    const consent = wsSettings && wsSettings.consentDefault || "legacy_allow";
    const s = c.schedule;
    const mode = s.mode === "sto" ? "now" : s.mode || "now";
    const out = [];
    const fix = (n, label) => ({
      label: label || "Fix it",
      onClick: () => this.goTo(n)
    });
    if (!(c.name || "").trim()) out.push({
      state: "warn",
      text: "Give the campaign a name so it can be saved.",
      fix: {
        label: "Add a name",
        onClick: () => {
          const el = document.querySelector(".ecc-name");
          if (el) el.focus();
        }
      }
    });
    const when = c.kind === "recurring" ? `every ${(s.cadence || {}).unit === "monthly" ? "month" : "week"}` : mode === "at" ? s.sendAt ? `on ${moment(s.sendAt).format("ddd, MMM D [at] h:mm A")}` : null : mode === "local" ? s.localDate ? `on ${moment(s.localDate, "YYYY-MM-DD").format("ddd, MMM D")} at ${localHourText(s.localHour, "h:mm A")} their time` : `at ${localHourText(s.localHour, "h:mm A")} their time` : "as soon as you send it";
    if (!hasRecipients(c.audience)) out.push({
      state: "warn",
      text: "Pick at least one list to send to.",
      fix: fix(1)
    });else if (estimate) out.push({
      state: Number(estimate.recipients) > 0 ? "ok" : "warn",
      text: `Goes to ${num(estimate.recipients)} ${Number(estimate.recipients) === 1 ? "person" : "people"}${when ? ` ${when}` : ""}${estimating ? " (recounting)" : ""}`,
      fix: Number(estimate.recipients) > 0 ? null : fix(1)
    });else out.push({
      state: "warn",
      text: estimating ? "Counting who it goes to…" : "Not counted yet.",
      fix: estimating ? null : {
        label: "Count now",
        onClick: this.estimate
      }
    });
    if (mode === "local" && s.localDate === "" && c.kind !== "recurring") out.push({
      state: "warn",
      text: "Pick the date it sends.",
      fix: fix(3)
    });
    if (mode === "at" && !s.sendAt && c.kind !== "recurring") out.push({
      state: "warn",
      text: "Pick the date and time it sends.",
      fix: fix(3)
    });
    if (s.mode === "sto") out.push({
      state: "warn",
      text: "\"Best time per person\" is gone, so this now sends as soon as it's scheduled.",
      fix: fix(3, "Change")
    });
    out.push(stepDone(c, 2) ? {
      state: "ok",
      text: "Subject and email are ready"
    } : {
      state: "warn",
      text: !(c.content.subject || "").trim() ? "The email needs a subject." : "The email has no content yet.",
      fix: fix(2)
    });
    if (c.kind === "ab_test") {
      const vs = c.variants.variants || [];
      out.push(vs.every(v => (v.subject || "").trim()) ? {
        state: "ok",
        text: `Testing ${vs.length} subjects: ${abTimeline(c.variants)}`
      } : {
        state: "warn",
        text: "Each version in the subject test needs a subject.",
        fix: fix(2)
      });
    }
    out.push(plunk ? {
      state: "ok",
      text: "Unsubscribe link and your address are added to the footer"
    } : {
      state: "ok",
      text: `${railLabel(c.rail)} adds its own unsubscribe link; Plunk's footer is added too`
    });
    out.push({
      state: "ok",
      text: consent === "opt_in_only" ? "Only goes to people who agreed to get email" : "Goes to everyone who hasn't opted out"
    });
    if (plunk) {
      if (!sender) out.push({
        state: "warn",
        text: "Pick who the email comes from.",
        fix: fix(3)
      });else if (!senderOk) out.push({
        state: "warn",
        text: /*#__PURE__*/React.createElement(React.Fragment, null, "The sender's domain (", sender.domain, ") is ", domainLabel(sender.domainAuthStatus), "; confirm it before scheduling."),
        fix: fix(3)
      });else out.push({
        state: "ok",
        text: `From ${sender.fromEmail}`
      });
    } else out.push({
      state: "ok",
      text: `Sent through ${railLabel(c.rail)} from its verified address`
    });
    out.push(st.testSent ? {
      state: "ok",
      text: `Test sent to ${(st.testSentTo || []).join(", ") || "you"}${st.testSentDate ? ` (${dateTime(st.testSentDate)})` : ""}`,
      fix: {
        label: "Send another",
        onClick: () => this.openTestForm()
      }
    } : {
      state: "warn",
      text: "Send yourself a test (needed before review).",
      fix: {
        label: "Send a test",
        onClick: () => this.openTestForm()
      }
    });
    const lr = lint.result;
    if (lr) out.push(lr.clean ? {
      state: "ok",
      text: "Links, unsubscribe, address and spam signals checked"
    } : {
      state: "warn",
      text: `The checks found ${num(lr.errors)} problem${lr.errors === 1 ? "" : "s"} and ${num(lr.warnings)} warning${lr.warnings === 1 ? "" : "s"}.`,
      fix: {
        label: lint.busy ? "Checking…" : "Check again",
        onClick: this.lint
      }
    });else out.push(st.lintClean === true ? {
      state: "ok",
      text: `Links, unsubscribe, address and spam signals checked (${dateTime(st.lintDate)})`,
      fix: {
        label: lint.busy ? "Checking…" : "Check again",
        onClick: this.lint
      }
    } : {
      state: "warn",
      text: st.lintClean === false ? "The last checks found problems." : "Links, unsubscribe, address and spam signals not checked yet.",
      fix: {
        label: lint.busy ? "Checking…" : "Check now",
        onClick: this.lint
      }
    });
    if (st.resend) out.push({
      state: "ok",
      text: `Follows up with people who didn't open on ${moment(resendDate(c)).format("MMM D, h:mm A")}, subject “${st.resend.subject || ""}”`
    });
    return out;
  }
  renderChecklist() {
    const {
      c,
      test,
      lint,
      busy,
      actionErr
    } = this.state;
    const {
      sendWithoutReview,
      canSchedule,
      canSubmit
    } = this.gate();
    const st = c.settings || {};
    const findings = lint.result && lint.result.findings || [];
    return /*#__PURE__*/React.createElement("div", {
      className: "ecc-card ecc-checklist"
    }, /*#__PURE__*/React.createElement("b", {
      className: "ecc-checklist-title"
    }, "Before it goes out"), this.checks().map((k, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "ecc-checkrow " + k.state
    }, /*#__PURE__*/React.createElement("span", {
      className: "m",
      "aria-hidden": "true"
    }, k.state === "ok" ? "✓" : "!"), /*#__PURE__*/React.createElement("span", null, k.text, k.fix ? /*#__PURE__*/React.createElement(React.Fragment, null, " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: k.fix.onClick
    }, k.fix.label)) : null))), lint.err ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note bad"
    }, lint.err) : null, findings.length ? /*#__PURE__*/React.createElement("ul", {
      className: "ecc-findings"
    }, findings.map((f, i) => /*#__PURE__*/React.createElement("li", {
      key: i,
      className: f.severity
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-sev " + f.severity
    }, f.severity === "error" ? "Problem" : f.severity === "warning" ? "Warning" : "Note"), " ", f.message, " ", /content|subject|image|text|link/i.test(String(f.code)) ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: () => this.goTo(2)
    }, "Fix it") : /sender|rail/i.test(String(f.code)) ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: () => this.goTo(3)
    }, "Fix it") : null))) : null, test.open ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-testform"
    }, /*#__PURE__*/React.createElement("input", {
      style: {
        ...INPUT,
        flex: "1 1 200px"
      },
      value: test.to,
      "aria-label": "Test addresses",
      placeholder: "Email addresses, separated by commas",
      onChange: e => this.setState({
        test: {
          ...test,
          to: e.target.value
        }
      })
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      disabled: test.busy,
      onClick: () => this.sendTest()
    }, test.busy ? "Sending…" : "Send test"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-textbtn",
      disabled: test.busy,
      onClick: () => this.setState({
        test: {
          ...test,
          open: false,
          err: null
        }
      })
    }, "Cancel")) : null, test.err ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note bad"
    }, test.err) : null, c.status === "Rejected" && c.rejectReason ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate bad"
    }, /*#__PURE__*/React.createElement("b", null, "Sent back:"), " ", c.rejectReason, ". Fix it and submit again.") : null, c.status === "Approved" ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate ok"
    }, "Approved by an administrator. Schedule it when you're ready.") : c.status === "InReview" ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate"
    }, "Waiting for an administrator to review it. Editing is paused until it's approved or sent back.") : sendWithoutReview ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate ok"
    }, "Your workspace can send without review.", canSubmit && canSchedule ? /*#__PURE__*/React.createElement(React.Fragment, null, " Want a second pair of eyes? ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      disabled: busy || !st.testSent,
      title: !st.testSent ? "Send yourself a test first" : "",
      onClick: () => this.transition("submit")
    }, "Submit for review")) : null) : /*#__PURE__*/React.createElement("div", {
      className: "ecc-gate"
    }, "An administrator approves campaigns before they go out. Submitting puts it in the review queue", isAdmin() ? /*#__PURE__*/React.createElement(React.Fragment, null, " (you can approve it in ", /*#__PURE__*/React.createElement(Link, {
      to: "/CampaignReview"
    }, "Campaign review"), ")") : null, "."), actionErr ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger",
      style: {
        margin: 0
      }
    }, actionErr) : null);
  }
  renderPreviewCard() {
    const {
      c,
      preview,
      previewPerson,
      test
    } = this.state;
    const st = c.settings || {};
    const testNote = test.err ? {
      kind: "error",
      text: test.err
    } : test.result ? {
      kind: "ok",
      text: `Test sent to ${(test.result && test.result.to || []).join(", ") || "you"}.`
    } : st.testSent ? {
      kind: "ok",
      text: `Last test ${st.testSentDate ? dateTime(st.testSentDate) : "sent"}.`
    } : null;
    const subject = preview && preview.subject || c.content.subject;
    return /*#__PURE__*/React.createElement("div", {
      className: "ecc-card ecc-previewcard cmp"
    }, subject || c.content.preheader ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-inboxline"
    }, /*#__PURE__*/React.createElement("b", null, subject || "No subject yet"), c.content.preheader ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", c.content.preheader) : null) : null, /*#__PURE__*/React.createElement(PreviewPane, {
      html: preview && preview.html,
      busy: !!(preview && preview.busy),
      renderErr: preview && preview.err ? preview.err : null,
      err: !stepDone(c, 2) ? "Add a subject and the email to see the preview." : null,
      person: previewPerson,
      onPerson: this.setPreviewPerson,
      onSendTest: this.sendTestToMe,
      testBusy: test.busy,
      testNote: testNote
    }), stepDone(c, 2) ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: () => this.preview(previewPerson ? previewPerson.id : null)
    }, "Refresh the preview"), " \xB7 exactly what people get, footer and personal details included.") : null);
  }
  section(n, title, right, body) {
    return /*#__PURE__*/React.createElement("section", {
      ref: this.sec[n],
      className: "ecc-section" + (this.state.flash === n ? " flash" : ""),
      "aria-labelledby": `ecc-sec-${n}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-section-head"
    }, /*#__PURE__*/React.createElement("span", {
      className: "ecc-num",
      "aria-hidden": "true"
    }, n), /*#__PURE__*/React.createElement("b", {
      id: `ecc-sec-${n}`
    }, title), /*#__PURE__*/React.createElement("span", {
      className: "ecc-spacer"
    }), right), body);
  }
  render() {
    if (!hasTenantFeature("emailAutomation")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Email isn't configured for this workspace."));
    }
    const {
      c,
      loadErr,
      saveState,
      savedAt,
      saveErr,
      estimate,
      estimating,
      editing,
      test
    } = this.state;
    if (loadErr) return /*#__PURE__*/React.createElement(Error, {
      message: loadErr
    });
    if (!c) return /*#__PURE__*/React.createElement(Loader, null);
    const locked = this.locked();
    const saveText = saveState === "saving" ? "saving…" : saveState === "saved" ? ago(savedAt) === "just now" ? "saved a moment ago" : `saved ${ago(savedAt)}` : saveState === "error" ? "couldn't save" : saveState === "locked" ? "locked" : c.id ? "saved" : (c.name || "").trim() ? "not saved yet" : "name it to save";
    const count = !hasRecipients(c.audience) ? null : estimate ? `${num(estimate.recipients)} ${Number(estimate.recipients) === 1 ? "person" : "people"}` : estimating ? "Counting…" : null;
    const startFrom = !locked ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-textbtn small",
      disabled: this.state.startFromBusy,
      onClick: () => this.setState({
        startFromOpen: !this.state.startFromOpen
      })
    }, this.state.startFromBusy ? "Copying…" : "Start from…") : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view ec-view ecc-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-top"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-back",
      "aria-label": "Back to Campaigns",
      onClick: this.back
    }, /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 16 16",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M10 3L5 8l5 5",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ecc-titleblock"
    }, /*#__PURE__*/React.createElement("input", {
      className: "ecc-name",
      value: c.name || "",
      placeholder: "Name this campaign",
      "aria-label": "Campaign name",
      autoFocus: !c.id,
      onChange: e => this.patch(null, {
        name: e.target.value
      })
    }), /*#__PURE__*/React.createElement("span", {
      className: "ecc-meta"
    }, "Campaign \xB7 ", statusLabel(c.status).toLowerCase(), " \xB7 ", saveText, saveState === "error" ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: this.save
    }, "Try again")) : null, c.id ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: `/Email/campaigns/detail/${c.id}`
    }, "Campaign page")) : null)), /*#__PURE__*/React.createElement("span", {
      className: "ecc-spacer"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn",
      disabled: test.busy,
      onClick: this.sendTestToMe
    }, test.busy ? "Sending…" : "Send me a test"), this.renderPrimary()), locked ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice warning ecc-banner"
    }, /*#__PURE__*/React.createElement("b", null, "This campaign is locked while ", c.status === "InReview" ? "in review" : String(c.status || "").toLowerCase(), "."), " ", saveErr || "Changes aren't saved. Unschedule it or make a copy from the campaign page to edit.", " ", /*#__PURE__*/React.createElement(Link, {
      to: `/Email/campaigns/detail/${c.id}`
    }, "Open the campaign")) : saveState === "error" ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger ecc-banner"
    }, saveErr, " ", /*#__PURE__*/React.createElement("a", {
      onClick: this.save
    }, "Try again")) : null, /*#__PURE__*/React.createElement("div", {
      className: "ecc-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-main"
    }, this.section(1, "Who it goes to", count ? /*#__PURE__*/React.createElement("span", {
      className: "ecc-count" + (estimating ? " pending" : "")
    }, count) : null, /*#__PURE__*/React.createElement(React.Fragment, null, this.renderPeople(), /*#__PURE__*/React.createElement(AudienceSection, {
      c: c,
      patch: this.patch,
      lists: this.state.lists,
      estimate: estimate,
      estimating: estimating,
      wsSettings: this.state.wsSettings
    }))), this.section(2, "What it says", startFrom, /*#__PURE__*/React.createElement(React.Fragment, null, this.state.startedFrom ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-note"
    }, "Started from \u201C", this.state.startedFrom.name, "\u201D.") : null, this.state.startFromOpen ? /*#__PURE__*/React.createElement(StartFromPicker, {
      items: this.state.pastCampaigns,
      load: this.loadPastCampaigns,
      onPick: r => {
        this.setState({
          startFromOpen: false
        });
        this.startFrom(r);
      },
      onClose: () => this.setState({
        startFromOpen: false
      })
    }) : null, /*#__PURE__*/React.createElement(ContentSection, {
      c: c,
      patch: this.patch,
      locked: locked,
      onEdit: this.openEditor
    }))), this.section(3, "When it sends", null, /*#__PURE__*/React.createElement(WhenSection, {
      c: c,
      patch: this.patch,
      wsSettings: this.state.wsSettings,
      senders: this.state.senders,
      railSenders: this.state.railSenders,
      rails: this.state.rails
    }))), /*#__PURE__*/React.createElement("aside", {
      className: "ecc-side"
    }, this.renderPreviewCard(), this.renderChecklist())), editing ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-editor",
      role: "dialog",
      "aria-modal": "true",
      "aria-label": "Edit the email"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-editor-top"
    }, /*#__PURE__*/React.createElement("b", null, "Edit the email"), /*#__PURE__*/React.createElement("span", {
      className: "ecc-meta"
    }, c.content.subject || "No subject yet", " \xB7 ", saveText), /*#__PURE__*/React.createElement("span", {
      className: "ecc-spacer"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      onClick: this.closeEditor
    }, "Done")), /*#__PURE__*/React.createElement("div", {
      className: "ecc-editor-scroll"
    }, /*#__PURE__*/React.createElement(ContentEditor, {
      c: c,
      patch: this.patch,
      brand: this.state.brand,
      renderPreview: this.renderPreview,
      contentPicks: this.state.contentPicks,
      loadContentPicks: this.loadContentPicks,
      pickContainer: this.pickContainer,
      hubAvailable: true,
      hubOpen: editing.hubOpen,
      onSendTest: this.sendTestToMe,
      testBusy: test.busy,
      testNote: null
    }))) : null);
  }
}
export default withRouter(EmailCampaignWizardComponent);